import { Button, Col, Form, Input, Typography } from 'antd'
import {
  useAssociateProjectProjectsQuery,
  useAssociateProjectWithCimsMutation,
} from '../../../../../../../schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from '../../../../../../../utils/defaults'
import CIMSFormItem from '../../../../../../../components/CIMSFormItem'
import CIMSSelect from '../../../../../../../components/CIMSSelect'
import { useOptions } from '../../../../../../../components/CIMSSelect/hooks/useOptions'
import { useState } from 'react'
import CIMSFormItemList from '../../../../../../../components/CIMSFormItemList'
import { an } from '@cims-online/shared'
import { useForm, useWatch } from 'antd/lib/form/Form'
import HelpIcon from '../../../../../../../components/HelpIcon'
import { simpleSuccessNotification } from '../../../../../../../hooks/notificationHandler'
import { useDevelopersModuleContext } from '../../DevelopersModuleContext'

const AssociateProject: React.VFC = () => {
  const {
    data,
    loading: fetching,
    refetch,
  } = useAssociateProjectProjectsQuery(DefaultQueryHookOptions)
  const moduleContext = useDevelopersModuleContext()
  const [mutating, setMutating] = useState(false)
  const [associate] = useAssociateProjectWithCimsMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      associateProjectWithCIMS: {
        projectMetadata: { project },
      },
    }) => {
      simpleSuccessNotification(
        `${
          project.projectName ?? project.globalId
        } successfully associated with CIMS Online!`
      )
      form.resetFields()
    },
  })

  const [userGroup, setUserGroup] = useState<{ label: string; value: string }>({
    label: 'Name',
    value: 'name',
  })
  const [form] = useForm<{
    projectId: string
    sharePointSiteName: string
    groups?: Array<{ id: string } | { name: string }>
    cmProjectLayersId?: number
    adminIds?: string[]
  }>()
  const groupsWatch = useWatch('groups', form)
  return (
    <Form
      form={form}
      style={{ maxWidth: '75%' }}
      onFinish={async ({
        projectId,
        sharePointSiteName,
        cmProjectLayersId,
        adminIds,
        ...values
      }) => {
        setMutating(true)
        const { ids, names } = (values.groups ?? []).reduce(
          (groups, value) => {
            if ('id' in value) groups.ids.push(value.id)
            if ('name' in value) groups.names.push(value.name)
            return groups
          },
          { ids: Array<string>(), names: Array<string>() }
        )
        await associate({
          variables: {
            input: {
              projectId,
              sharePointSiteName,
              cmProjectLayersId,
              userGroupIds: ids.length ? ids : undefined,
              userGroupNames: names.length ? names : undefined,
              adminIds,
            },
          },
        })
        setMutating(false)
        refetch()
      }}
    >
      <Typography.Paragraph>
        Fill out the following form to associate a Project with CIMS.
      </Typography.Paragraph>
      <CIMSFormItem
        name="projectId"
        label="Project"
        requiredMessage="Please select a Project"
      >
        <CIMSSelect
          loading={fetching}
          simple
          options={useOptions({
            items: data?.projects?.records?.filter(
              ({ projectMetadata }) => projectMetadata === null
            ),
            label: ({ projectName, globalId }) => projectName ?? globalId,
            value: 'globalId',
            sort: 'globalId',
            tooltip: 'globalId',
          })}
        />
      </CIMSFormItem>
      <CIMSFormItem
        name="sharePointSiteName"
        label="SharePoint Site Name"
        requiredMessage="Please enter a SharePoint site name"
        help="The name of the SharePoint site that appears after /site in the SharePoint site webURL. For example https://horrocksengineersinc.sharepoint.com/sites/{sharePointSiteName}"
      >
        <Input allowClear />
      </CIMSFormItem>
      <CIMSFormItem name="cmProjectLayersId" label="CM Project Layer">
        <CIMSSelect
          simple
          loading={fetching}
          options={useOptions({
            items: data?.cmProjectLayers,
            sort: 'name',
            value: 'id',
            label: 'name',
            tooltip: ({ id }) => id.toString(),
          })}
        />
      </CIMSFormItem>
      <CIMSFormItem
        name="adminIds"
        label="Admins"
        help="Users to assign as Project Admins. Be sure to only select users who will be members of the project's User Groups"
      >
        <CIMSSelect
          simple
          mode="multiple"
          loading={moduleContext.loading}
          options={useOptions({
            items: moduleContext.users,
            sort: 'username',
            value: 'id',
            label: 'username',
            tooltip: ({ mail, username }) =>
              username === mail ? undefined : mail,
          })}
        />
      </CIMSFormItem>
      <div style={{ marginBottom: '1rem' }}>
        <CIMSFormItemList
          name="groups"
          label="Group"
          help="a list of Azure User Group to associate with the Project"
          buttons={[
            { icon: 'add', add: () => ({ [userGroup.value]: undefined }) },
          ]}
          validate={(values) =>
            values?.length
              ? true
              : `Please add at least one Group ${userGroup.value}`
          }
          buttonRowExtras={{
            position: 'after',
            children: (
              <HelpIcon text="Whether to enter User Groups by ID or Name">
                <CIMSSelect
                  style={{ width: 100 }}
                  options={[
                    { label: 'ID', value: 'id' },
                    { label: 'Name', value: 'name' },
                  ]}
                  defaultValue="name"
                  allowClear={false}
                  onChange={(value) => setUserGroup(value as any)}
                  disabled={(groupsWatch?.length ?? 0) > 0}
                />
              </HelpIcon>
            ),
          }}
        >
          {(name) => (
            <Col flex={1}>
              <CIMSFormItem
                name={[name, userGroup.value]}
                label={userGroup.label}
                requiredMessage={`Please enter ${an(userGroup.value)}`}
              >
                <Input allowClear />
              </CIMSFormItem>
            </Col>
          )}
        </CIMSFormItemList>
      </div>
      <Button
        disabled={fetching || mutating}
        loading={mutating}
        htmlType="submit"
        type="primary"
      >
        Associate
      </Button>
    </Form>
  )
}

export default AssociateProject
